import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Select,
  Modal,
  Typography,
  Button,
  notification,
  DatePicker,
} from "antd";
import DepartmentsApi from "../../Apis/DepartmentsApi";
import ProductApi from "../../Apis/ProductApi";
import dayjs from "dayjs";
import moment from "moment";
import AllergensApi from "../../Apis/AllergensApi";
import StorageSuggestionApi from "../../Apis/StorageSuggestionApi";
import CoolingShelfApi from "../../Apis/CoolingShelfApi";
import ShelfLifeApi from "../../Apis/ShelfLifeApi";
import IngredientsApi from "../../Apis/IngredientsApi";
import DepartmentModel from "../../DropDowns/DepartmentModel";
import SupplierModel from "../../DropDowns/SupplierModel";
import AllergenModel from "../../DropDowns/AllergenModel";
import CoolingShelfModel from "../../DropDowns/CoolingShelfModel";
import ShelfLifeModel from "../../DropDowns/ShelfLifeModel";
import StorageSuggestionModel from "../../DropDowns/StorageSuggestionModel";
import UOM1 from "../../DropDowns/Messurements/UOM1";
import UOM2 from "../../DropDowns/Messurements/UOM2";
import UOM3 from "../../DropDowns/Messurements/UOM3";
import StorageDropDown from "../../DropDowns/StorageDropDown";

const { Title } = Typography;

function EditProduct({ visible, onClose, Product, Ingredient }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const CURRENCY = decodedToken.currency === "USD" ? "$" : "€";
  const [coolingShelfResponse, setCoolingShelfResponse] = useState(null);
  const [ShelfResponse, setShelfResponse] = useState(null);
  const [StorageSuggestionResponse, setStorageSuggestionResponse] = useState(null);
  const [StorageResponse, setStorageResponse] = useState(null);
  const [Allergens, setAllergens] = React.useState([]);
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [groupedProductsData, setGroupedProductsData] = useState([]);
  const [originalFullBarcode, setOriginalFullBarcode] = useState(null);
  const [originalNumericBarcode, setOriginalNumericBarcode] = useState(null);
  const [barcodePrefix, setBarcodePrefix] = useState(null);

  const getAllergens = useCallback(() => {
    AllergensApi.ListAllergensApi(0,100)
      .then((response) => {
        setAllergens(response?.payloadDto);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // Extract numeric code after # if exists, otherwise get the whole code
  const extractNumericCode = (productCode) => {
    if (!productCode) return null;
    const hashMatch = productCode.match(/#(\d+)$/);
    if (hashMatch) {
      return hashMatch[1];
    }
    return productCode;
  };

  // Get the prefix before # if exists, otherwise null
  const getBarcodePrefix = (productCode) => {
    if (!productCode) return null;
    const hashMatch = productCode.match(/(.*)#\d+$/);
    if (hashMatch) {
      return hashMatch[1];
    }
    return null;
  };

  // Check if barcode has # separator
  const hasHashSeparator = (productCode) => {
    return productCode && productCode.includes('#');
  };

  // Get grouped products by barcode with their full data
  const getGroupedProducts = useCallback(async (productCode) => {
    const numericCode = extractNumericCode(productCode);
    if (!numericCode) return [];

    try {
      const response = await ProductApi.GetByBarCode(numericCode);
      const products = response?.payloadDto || [];
      // Filter out the current product
      const filteredProducts = products.filter(p => p.id !== Product?.id);
      
      // Fetch full details for each grouped product to get their cost prices and suppliers
      const fullProductData = await Promise.all(
        filteredProducts.map(async (p) => {
          try {
            const detailResponse = await ProductApi.SingleProducts(p.id);
            return detailResponse?.payload || p;
          } catch (err) {
            console.error(`Error fetching product ${p.id}:`, err);
            return p;
          }
        })
      );
      
      setGroupedProductsData(fullProductData);
      return fullProductData;
    } catch (error) {
      console.error("Error fetching grouped products:", error);
      return [];
    }
  }, [Product?.id]);

  const getShelfLifes = useCallback(() => {
    if (Product?.shelfLifeDTO?.id) {
      ShelfLifeApi.SingleShelfLife(Product?.shelfLifeDTO?.id)
        .then((response) => {
          setShelfResponse(response.payload);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [Product?.shelfLifeDTO?.id]);

  const getCoolingShelfLifes = useCallback(() => {
    if (Product?.coolingShelfLifeDTO?.id) {
      CoolingShelfApi.SingleCoolingProcess(Product?.coolingShelfLifeDTO?.id)
        .then((response) => {
          setCoolingShelfResponse(response.payload);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [Product?.coolingShelfLifeDTO?.id]);

  const getStorage = useCallback(() => {
    if (Product?.storageSuggestionDTO?.id) {
      StorageSuggestionApi.Singlestorage(Product?.storageSuggestionDTO?.id)
        .then((response) => {
          setStorageSuggestionResponse(response.payload);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [Product?.storageSuggestionDTO?.id]);

  useEffect(() => {
    getAllergens();
    getShelfLifes();
    getCoolingShelfLifes();
    getStorage();

    if (Product?.storageDTO) {
      setStorageResponse(Product.storageDTO);
    }

    if (Product?.storageSuggestionDTO) {
      setStorageSuggestionResponse(Product.storageSuggestionDTO);
    }

    if (Product?.coolingShelfLifeDTO) {
      setCoolingShelfResponse(Product.coolingShelfLifeDTO);
    }

    if (Product?.shelfLifeDTO) {
      setShelfResponse(Product.shelfLifeDTO);
    }

    // Store original barcode information
    if (Product?.productCode) {
      const fullBarcode = Product.productCode;
      setOriginalFullBarcode(fullBarcode);
      
      const numericCode = extractNumericCode(fullBarcode);
      setOriginalNumericBarcode(numericCode);
      
      const prefix = getBarcodePrefix(fullBarcode);
      setBarcodePrefix(prefix);
      
      // Fetch grouped products
      getGroupedProducts(fullBarcode).then(products => {
        setGroupedProducts(products);
      });
    }

    form.setFieldsValue({
      productName: Product?.productName,
      productCode: Product?.productCode?.split("#")[1] || Product?.productCode,
      unit: Product?.unitUomOne,
      unit2: Product?.unitUomTwo,
      unit3: Product?.unitUomThree,
      unitOfMeasurement1: Product?.unitOfMeasurement1,
      expiryDate: dayjs(Product?.expiryDate),
      unitOfMeasurement2: Product?.unitOfMeasurement2,
      unitOfMeasurement3: Product?.unitOfMeasurement3,
      costPriceUoM1: Product?.costPriceUoM1,
      costPriceUoM2: Product?.costPriceUoM2,
      costPriceUoM3: Product?.costPriceUoM3,
      sellingPriceUoM1: Product?.sellingPriceUoM1,
      sellingPriceUoM2: Product?.sellingPriceUoM2,
      sellingPriceUoM3: Product?.sellingPriceUoM3,
      institutionId: Product?.institutionDTO?.id,
      ShelfLifeID: Product?.shelfLifeDTO?.id,
      CoolingShelfLifeID: Product?.coolingShelfLifeDTO?.id,
      StorageID: Product?.storageDTO?.id,
      StorageSuggessionID: Product?.storageSuggestionDTO?.id,
      supplierId: Product?.supplierDTO?.map(item => item.id),
      createDate: dayjs(Product?.productCreateDate),
      OUBD: dayjs(parseInt(Product?.openUBD)),
      Allergen: Product?.allergens?.map(item => item.id),
    });

  }, [Product, getShelfLifes, getCoolingShelfLifes, getStorage, getGroupedProducts]);

  const validateInputNumber = (_, value) => {
    if (value >= 0) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Value must be greater than to 0"));
  };

  const handleCoolingShelfResponse = (response) => {
    setCoolingShelfResponse(response);
  };

  const handleStorageResponse = (response) => {
    setStorageResponse(response);
    console.log("Storage response updated:", response);
  };

  const handleShelfResponse = (response) => {
    setShelfResponse(response);
  };

  const handleStorageSuggestionResponse = (response) => {
    setStorageSuggestionResponse(response);
  };

const handleSubmit = (data) => {
  const SelectDate = dayjs(data.createDate).format("DD/MM/YYYY");
  const expiryDate = dayjs(data.expiryDate).format("DD/MM/YYYY");
  const OUBD = dayjs(data.OUBD).format("DD/MM/YYYY");
  
  const allergenNames = Allergens
    .filter(item => data?.Allergen.includes(item.id))
    .map(item => item.id);

  // Determine the new full barcode
  let newFullBarcode;
  const newNumericBarcode = data.productCode;
  
  if (hasHashSeparator(originalFullBarcode)) {
    newFullBarcode = `${barcodePrefix}#${newNumericBarcode}`;
  } else {
    newFullBarcode = newNumericBarcode;
  }

  // SHARED UOM values (these will be the same for all products)
  const sharedUom1 = data.unitOfMeasurement1 || 1;
  const sharedUom2 = data.unitOfMeasurement2 || 1;
  const sharedUom3 = data.unitOfMeasurement3 || 1;
  const sharedUnit1 = data.unit;
  const sharedUnit2 = data.unit2;
  const sharedUnit3 = data.unit3;

  // Build the base form data with SHARED UOMs
  const formData = {
    institutionId: data.institutionId,
    productCreateDate: convertToUnixTimestamp(SelectDate),
    expiryDate: convertToUnixTimestamp(expiryDate),
    productName: data.productName,
    productCode: newFullBarcode,
    currency: CURRENCY,
    unitUomOne: sharedUnit1,
    unitUomTwo: sharedUnit2,
    unitUomThree: sharedUnit3,
    unitOfMeasurement1: sharedUom1,
    unitOfMeasurement2: sharedUom2,
    unitOfMeasurement3: sharedUom3,
    costPriceUoM1: data.costPriceUoM1,
    costPriceUoM2: data.costPriceUoM2,
    costPriceUoM3: data.costPriceUoM3,
    sellingPriceUoM1: data.sellingPriceUoM1 || 0,
    sellingPriceUoM2: data.sellingPriceUoM2 || 0,
    sellingPriceUoM3: data.sellingPriceUoM3 || 0,
    department: "",
    allergen: data?.Allergen?.join(','),
    supplierDTO: data.supplierId.map((number) => ({ id: number })),
    allergens: data.Allergen.map((number) => ({ id: number })),
    openUBD: convertToUnixTimestamp(OUBD),
    shelfLifeDTO: (data?.ShelfLifeID) ? {
      id: data?.ShelfLifeID,
      period: ShelfResponse?.period,
      periodType: ShelfResponse?.periodType,
      tenantDTO: {
        id: ShelfResponse?.tenantDTO?.id,
        tenantApiKey: ShelfResponse?.tenantDTO?.tenantApiKey,
      },
    } : null,
    allergensDTO: {
      id: data?.Allergen[0],
    },
    coolingShelfLifeDTO: (data?.CoolingShelfLifeID) ? {
      id: data?.CoolingShelfLifeID,
      period: coolingShelfResponse?.period,
      periodType: coolingShelfResponse?.periodType,
      temperatureInCelsius: coolingShelfResponse?.temperatureInCelsius,
      tenantDTO: {
        id: coolingShelfResponse?.tenantDTO?.id,
        tenantApiKey: coolingShelfResponse?.tenantDTO?.tenantApiKey,
      },
    } : null,
    institutionDTO: {
      id: data.institutionId,
    },
    storageSuggestionDTO: {
      id: StorageSuggestionResponse?.id,
      description: StorageSuggestionResponse?.description,
      name: StorageSuggestionResponse?.name,
      tenantDTO: {
        id: StorageSuggestionResponse?.tenantDTO?.id,
        tenantApiKey: StorageSuggestionResponse?.tenantDTO?.tenantApiKey,
      },
    },
    storageDTO: {
      id: data?.StorageID || StorageResponse?.id,
    },
    tenantDTO: {
      id: decodedToken?.tenantId,
      tenantApiKey: decodedToken?.tenantApiKey,
    },
  };

  // Helper function to create form data for a product with SHARED UOMs but INDEPENDENT prices
  const createProductFormDataWithSharedUOMs = (product, baseFormData, isCurrentProduct = false) => {
    let ownCostPriceUoM1;
    let ownSellingPriceUoM1;
    let ownSellingPriceUoM2;
    let ownSellingPriceUoM3;
    
    if (isCurrentProduct) {
      ownCostPriceUoM1 = parseFloat(baseFormData.costPriceUoM1) || 0;
      ownSellingPriceUoM1 = parseFloat(baseFormData.sellingPriceUoM1) || 0;
      ownSellingPriceUoM2 = parseFloat(baseFormData.sellingPriceUoM2) || 0;
      ownSellingPriceUoM3 = parseFloat(baseFormData.sellingPriceUoM3) || 0;
    } else {
      ownCostPriceUoM1 = parseFloat(product.costPriceUoM1) || 0;
      ownSellingPriceUoM1 = parseFloat(product.sellingPriceUoM1) || 0;
      ownSellingPriceUoM2 = parseFloat(product.sellingPriceUoM2) || 0;
      ownSellingPriceUoM3 = parseFloat(product.sellingPriceUoM3) || 0;
    }
    
    const costPriceUoM2 = sharedUom2 > 0 ? ownCostPriceUoM1 / sharedUom2 : 0;
    const costPriceUoM3 = (sharedUom2 > 0 && sharedUom3 > 0) ? costPriceUoM2 / sharedUom3 : 0;

    let productFullBarcode;
    if (hasHashSeparator(product.productCode)) {
      const prefix = getBarcodePrefix(product.productCode);
      productFullBarcode = `${prefix}#${newNumericBarcode}`;
    } else {
      productFullBarcode = newNumericBarcode;
    }

    const supplierIds = product.supplierDTO?.map(s => s.id) || [];
    const supplierDTO = supplierIds.map((id) => ({ id }));

    const allergenIds = product.allergens?.map(a => a.id) || [];
    const allergensDTO = allergenIds.map((id) => ({ id }));

    return {
      ...baseFormData,
      productCode: productFullBarcode,
      unitUomOne: sharedUnit1,
      unitUomTwo: sharedUnit2,
      unitUomThree: sharedUnit3,
      unitOfMeasurement1: sharedUom1,
      unitOfMeasurement2: sharedUom2,
      unitOfMeasurement3: sharedUom3,
      costPriceUoM1: ownCostPriceUoM1,
      costPriceUoM2: costPriceUoM2.toFixed(2),
      costPriceUoM3: costPriceUoM3.toFixed(2),
      sellingPriceUoM1: ownSellingPriceUoM1,
      sellingPriceUoM2: ownSellingPriceUoM2,
      sellingPriceUoM3: ownSellingPriceUoM3,
      supplierDTO: supplierDTO,
      allergens: allergenIds.map((id) => ({ id })),
    };
  };

  const barcodeChanged = newNumericBarcode !== originalNumericBarcode;

  // Update the current product
  ProductApi.PutProduct(Product?.id, JSON.stringify(formData))
    .then((response) => {
      let updatePromises = [];

      if (barcodeChanged) {
        return ProductApi.GetByBarCode(newNumericBarcode)
          .then((barcodeResponse) => {
            const productsWithNewBarcode = barcodeResponse?.payloadDto || [];
            const otherProducts = productsWithNewBarcode.filter(p => p.id !== Product?.id);
            
            const currentProductData = createProductFormDataWithSharedUOMs(Product, formData, true);
            updatePromises.push(
              ProductApi.PutProduct(Product?.id, JSON.stringify(currentProductData))
            );
            
            otherProducts.forEach(product => {
              const productFormData = createProductFormDataWithSharedUOMs(product, formData, false);
              updatePromises.push(
                ProductApi.PutProduct(product.id, JSON.stringify(productFormData))
              );
            });

            return ProductApi.GetByBarCode(originalNumericBarcode)
              .then((oldBarcodeResponse) => {
                const productsWithOldBarcode = oldBarcodeResponse?.payloadDto || [];
                const oldProducts = productsWithOldBarcode.filter(p => p.id !== Product?.id);
                
                oldProducts.forEach(product => {
                  const productFormData = createProductFormDataWithSharedUOMs(product, formData, false);
                  updatePromises.push(
                    ProductApi.PutProduct(product.id, JSON.stringify(productFormData))
                  );
                });

                return Promise.all(updatePromises);
              });
          });
      } else {
        const currentProductData = createProductFormDataWithSharedUOMs(Product, formData, true);
        updatePromises.push(
          ProductApi.PutProduct(Product?.id, JSON.stringify(currentProductData))
        );
        
        groupedProductsData.forEach(groupProduct => {
          const productFormData = createProductFormDataWithSharedUOMs(groupProduct, formData, false);
          updatePromises.push(
            ProductApi.PutProduct(groupProduct.id, JSON.stringify(productFormData))
          );
        });

        return Promise.all(updatePromises);
      }
    })
    .then((responses) => {
      const currentProductResponse = responses.find(r => r?.payload?.id === Product?.id);
      
      let form2 = JSON.stringify({
        name: data.productName,
        ingredientsStatus: "OPENED",
        batchCode: newNumericBarcode,
        allergensDTO: allergenNames.map((number) => ({ id: number })),
        productDTO: {
          id: currentProductResponse?.payload?.id || Product?.id,
        },
        tenantDTO: {
          id: decodedToken?.tenantId,
          tenantApiKey: decodedToken?.tenantApiKey,
        },
        institutionDTO: {
          id: data?.institutionId,
        },
      });

      return IngredientsApi.PutIngredients(Ingredient?.id, form2);
    })
    .then(() => {
      notification.open({
        message: <span style={{ fontWeight: "bold" }}>Successfully</span>,
        description: `Updated successfully. All products share the same UOM values while maintaining their own prices.`,
        closeIcon: false,
        duration: 4,
        style: {
          backgroundColor: "#b3ffb3",
          color: "green",
        },
      });

      // ADD: Close modal and reset form
      form.resetFields();
      onClose(); // This will close the modal
      
    })
    .catch((err) => {
      console.log(err);
      if (err.apiError?.status) {
        notification.open({
          message: <span style={{ fontWeight: "bold" }}>Error</span>,
          description: err.apiError.message,
          closeIcon: false,
          duration: 3,
          style: {
            backgroundColor: "#fff2f0",
            color: "red",
          },
        });
      }
    });
};

  function convertToUnixTimestamp(dateString) {
    const parts = dateString.split("/");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    const unixTimestamp = Date.UTC(year, month, day);
    return unixTimestamp;
  }

  const currentDate = moment().format("DD/MM/YYYY");

  const recalcCosts = () => {
    const costPriceUoM1 = parseFloat(form.getFieldValue('costPriceUoM1'));
    const uom2 = parseFloat(form.getFieldValue('unitOfMeasurement2'));
    const uom3 = parseFloat(form.getFieldValue('unitOfMeasurement3'));

    if (!costPriceUoM1 || !uom2 || !uom3) return;

    const costPriceUoM2 = costPriceUoM1 / uom2;
    const costPriceUoM3 = costPriceUoM2 / uom3;

    form.setFieldsValue({
      costPriceUoM2: costPriceUoM2.toFixed(2),
      costPriceUoM3: costPriceUoM3.toFixed(2),
    });
  };

  return (
    <>
      <Modal
        footer={[]}
        width={600}
        visible={visible}
        onCancel={onClose}
        onOk={onClose}
        maskClosable={false}
      >
        <Title level={4} className="text-center Primary-Font mb-4 pb-3">
          Edit Individual Product
        </Title>
        {groupedProductsData.length > 0 && (
          <div style={{ 
            backgroundColor: '#f6ffed', 
            padding: '8px 12px', 
            borderRadius: '4px',
            marginBottom: '16px',
            border: '1px solid #b7eb8f'
          }}>
            <span style={{ fontSize: '13px', color: '#52c41a' }}>
              <strong>Note:</strong> {groupedProductsData.length} similar product(s) will also be updated.
            </span>
          </div>
        )}
        <Form
          form={form}
          name="nest-messages"
          layout="horizontal"
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 16,
          }}
          className="FormClass IndividualProduct-Model"
          onFinish={handleSubmit}
        >
          <>
            <div className="Product-createDate">
              <div className="mb-3" style={{ display: 'flex', fontSize: '18px', alignItems: 'center' }}>
                <b className="mx-2">{currentDate}</b>
                <img
                  src="/assest/Images/cal.png"
                  style={{ width: '32px' }}
                  className="logo-png"
                />
              </div>
              <Form.Item
                name="createDate"
                className="text-lable col-lg-6 col-sm-12 col-md-7 d-none"
                rules={[
                  {
                    required: true,
                    message: "Please enter Date.",
                  },
                ]}
              >
                <DatePicker
                  format={"DD/MM/YYYY"}
                  className="form-control control-input"
                />
              </Form.Item>
            </div>
            <Form.Item
              name="productName"
              label="Product Name :"
              className="text-lable"
              rules={[
                {
                  required: true,
                  message: "Please enter Product Name.",
                },
              ]}
            >
              <Input
                type="text"
                placeholder="Product Name"
                className="control-input"
              />
            </Form.Item>
            <Form.Item
              name="productCode"
              label="Barcode :"
              className="text-lable pt-3"
              rules={[
                {
                  required: true,
                  message: "Please enter Barcode.",
                },
              ]}
            >
              <Input
                type="text"
                placeholder="Barcode (numeric part only)"
                className="control-input"
              />
            </Form.Item>
            <div className="row product-row d-flex pt-3">

              <div className=" col-2"></div>
              <div className=" col-6">
              <Form.Item
                  name="unitOfMeasurement1"
                  label="UoM 1"
                  className="text-lable producectCol disabled"
                >
                  <Input
                    disabled
                    type="text"
                    inputMode="decimal"
                    style={{ 
                      MozAppearance: 'textfield'
                    }}
                    placeholder="0.00"
                    className="control-input"
                  />
                </Form.Item>
              </div>
              <div className="col-4">
                <Form.Item
                  name="unit"
                  label="Unit"
                  className="text-lable producectCol disabled"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Unit",
                    },
                  ]}
                >

                  <UOM1 disabled={true} />
                </Form.Item>
              </div>
            </div>
            <div className="row product-row d-flex pt-1">

              <div className=" col-2"></div>
              <div className=" col-6">
              <Form.Item
                name="unitOfMeasurement2"
                label="UoM 2"
                className="text-lable producectCol"
                rules={[
                  {
                    required: true,
                    message: "Please enter unit of measurement 2.",
                  },
                ]}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  style={{ 
                    MozAppearance: 'textfield'
                  }}
                  placeholder="0.00"
                  className="control-input"
                  onKeyDown={(e) => {
                    const key = e.key;
                    const currentValue = e.target.value;
                    
                    const controlKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
                                        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                                        'Home', 'End'];
                    
                    if (controlKeys.includes(key)) {
                      return;
                    }
                    
                    if (/^[0-9]$/.test(key)) {
                      return;
                    }
                    
                    if (key === '.' && !currentValue.includes('.')) {
                      return;
                    }
                    
                    e.preventDefault();
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      form.setFieldsValue({
                        unitOfMeasurement2: value === '' ? undefined : parseFloat(value) || 0
                      });
                      recalcCosts();
                    }
                  }}
                />
              </Form.Item>
              </div>
              <div className="col-4">
                <Form.Item
                  name="unit2"
                  label="Unit"
                  className="text-lable producectCol disabled"
                >
                  <UOM2 disabled={true} />
                </Form.Item>
              </div>
            </div>
            <div className="row product-row d-flex pt-1">

              <div className=" col-2"></div>
              <div className=" col-6">
              <Form.Item
                name="unitOfMeasurement3"
                label="UoM 3"
                className="text-lable producectCol"
                rules={[
                  {
                    required: true,
                    message: "Please enter unit of measurement 3.",
                  },
                ]}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  style={{ 
                    MozAppearance: 'textfield'
                  }}
                  placeholder="0.00"
                  className="control-input"
                  onKeyDown={(e) => {
                    const key = e.key;
                    const currentValue = e.target.value;
                    
                    const controlKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
                                        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                                        'Home', 'End'];
                    
                    if (controlKeys.includes(key)) {
                      return;
                    }
                    
                    if (/^[0-9]$/.test(key)) {
                      return;
                    }
                    
                    if (key === '.' && !currentValue.includes('.')) {
                      return;
                    }
                    
                    e.preventDefault();
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      form.setFieldsValue({
                        unitOfMeasurement3: value === '' ? undefined : parseFloat(value) || 0
                      });
                      recalcCosts();
                    }
                  }}
                />
              </Form.Item>
              </div>
              <div className="col-4">
                <Form.Item
                  name="unit3"
                  label="Unit"
                  className="text-lable producectCol disabled"
                >
                  <UOM3 disabled={true} />
                </Form.Item>
              </div>
            </div>
            <Form.Item
              name="costPriceUoM1"
              label="Cost Price per UoM 1 :"
              className="text-lable"
              rules={[
                {
                  required: true,
                  message: "Please enter Cost Price per UoM 1.",
                },
                { validator: validateInputNumber },
              ]}
            >
              <Input
                type="number"
                placeholder="Cost Price per UoM 1"
                className="control-input PriceClass"
                onChange={recalcCosts}
                style={{
                  border: 'none',
                  marginBottom: '0px',
                }}
                addonBefore={
                  <Button
                    style={{
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {CURRENCY}
                  </Button>
                }
              />
            </Form.Item>

            <Form.Item
              name="costPriceUoM2"
              label="Cost Price per UoM 2 :"
              className="text-lable disabled"
              rules={[
                {
                  required: true,
                  message: "Please enter Cost Price per UoM 2.",
                },
                { validator: validateInputNumber },
              ]}
            >
              <Input
                disabled
                type="number"
                placeholder="Cost Price per UoM 2"
                className="control-input"
                style={{
                  border: 'none',
                  marginBottom: '0px',
                }}
                addonBefore={
                  <Button
                    style={{
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {CURRENCY}
                  </Button>
                }
              />
            </Form.Item>

            <Form.Item
              name="costPriceUoM3"
              label="Cost Price per UoM 3 :"
              className="text-lable disabled"
              rules={[
                {
                  required: true,
                  message: "Please enter Cost Price per UoM 3.",
                },
                { validator: validateInputNumber },
              ]}
            >
              <Input
                disabled
                type="number"
                placeholder="Cost Price per UoM 3"
                className="control-input"
                style={{
                  border: 'none',
                  marginBottom: '0px',
                }}
                addonBefore={
                  <Button
                    style={{
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {CURRENCY}
                  </Button>
                }
              />
            </Form.Item>

            <SupplierModel disabled={true} />

            <DepartmentModel />

            <AllergenModel />

            <ShelfLifeModel onShelfResponse={handleShelfResponse} />

            <CoolingShelfModel onCoolingShelfResponse={handleCoolingShelfResponse} />

            <StorageSuggestionModel onStorageSuggestionResponse={handleStorageSuggestionResponse} />

            <StorageDropDown onStorageResponse={handleStorageResponse} />

            <div className="d-flex justify-content-center pt-4">
              <Button
                type="primary"
                htmlType="submit"
                className="btn btn-primary btn-save mx-2"
                style={{ width: "37%" }}
              >
                Update
              </Button>
              <Link
                type="primary"
                to={""}
                className="btn btn-primary btn-back mx-2 w-20"
                style={{ width: "37%" }}
                onClick={onClose}
              >
                Cancel
              </Link>
            </div>
          </>
        </Form>
      </Modal>
    </>
  );
}

export default EditProduct;